import React, { useState } from 'react';
import { Box, Alert, alpha, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import MarginBottomContainer from '../../../../components/containers/MarginBottomContainer';

import FullPageCircularProgress from '../../../../components/progressBar/FullPageCircularProgress';
import { usePracticeCollectionDetails } from '../../hooks/usePracticeCollectionDetails';
import { update } from '../../../../services/practice-collection-service';

import Header from './Header';
import SummaryCard from './SummaryCard';
import PracticesGrid from './PracticesGrid';

const PracticeCollectionDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    content,
    loading,
    error,
    readingCategories,
    refresh
  } = usePracticeCollectionDetails();

  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  if (loading) {
    return <FullPageCircularProgress />;
  }

  if (error) {
    return (
      <MarginBottomContainer>
        <Box sx={{ mt: 3 }}>
          <Alert
            severity="error"
            sx={{
              background: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              '& .MuiAlert-icon': {
                color: theme.palette.error.main
              }
            }}
          >
            {error}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2, color: theme.palette.text.primary }}
            onClick={() => navigate('/practice-collections')}
          >
            Back to Collections
          </Button>
        </Box>
      </MarginBottomContainer>
    );
  }

  if (!content) return null;

  const handleEditOpen = () => {
    setEditTitle(content.title || '');
    setEditDescription(content.description || '');
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    setEditLoading(true);
    try {
      await update(content.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        practices: content.practices?.map(p => p.id) || []
      });
      toast.success('Collection updated successfully');
      setEditOpen(false);
      refresh();
    } catch (err) {
      toast.error(`Update failed: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  // Map the collection status to a tabType so the ContentCard navigation knows where it originated
  // (e.g. if collection is DRAFT, clicking an SRP goes to /reading-practices/draft/id)
  const mapStatusToTabType = (status) => {
    switch(status) {
      case 'PUBLISHED': return 'published';
      case 'APPROVED': return 'approved';
      case 'RELEASED': return 'released';
      case 'ARCHIVED': return 'archived';
      case 'DRAFT':
      default:
        return 'draft';
    }
  };

  const tabType = mapStatusToTabType(content.status);

  return (
    <MarginBottomContainer>
      <Box>
        <Header 
          title={content.title} 
          onBack={() => navigate('/practice-collections')} 
          collectionId={content.id}
          status={content.status}
          onActionComplete={(action) => {
            if (action === 'delete') {
              toast.success('Collection deleted successfully');
              navigate('/practice-collections');
            } else {
              refresh();
            }
          }}
          onEditClick={handleEditOpen}
        />
        <SummaryCard content={content} />
        <PracticesGrid 
          practices={content.practices} 
          readingCategories={readingCategories} 
          tabType={tabType}
        />
      </Box>

      {/* Edit Details Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleEditSave}>
          <DialogTitle>Edit Collection Details</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="title"
              label="Collection Title"
              type="text"
              fullWidth
              variant="outlined"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              disabled={editLoading}
              required
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              margin="dense"
              id="description"
              label="Description (Optional)"
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              disabled={editLoading}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleEditClose} color="inherit" disabled={editLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={editLoading}>
              {editLoading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </MarginBottomContainer>
  );
};

export default PracticeCollectionDetails;
